import { config } from '@/config';
import { WebSocketMessage } from '@/types';

type MessageHandler = (message: WebSocketMessage) => void;

export { MessageHandler as MessageHandlerType } from './messageHandler';

export type ConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

export interface StatusInfo {
  /** True when this 'connected' notification follows a previous successful connection */
  isReconnect?: boolean;
}

type StatusListener = (state: ConnectionState, info?: StatusInfo) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers: MessageHandler[] = [];
  private statusListeners: StatusListener[] = [];
  private currentRoomId: string | null = null;
  private currentUserId: string | null = null;
  private hasConnectedBefore = false;

  /**
   * Connect to a room. Safe to call multiple times: if a socket for the same
   * room/user is already open or connecting this is a no-op, and switching
   * rooms cleanly tears down the previous socket without triggering the
   * auto-reconnect logic.
   */
  connect(roomId: string, userId: string) {
    const sameSession =
      this.currentRoomId === roomId && this.currentUserId === userId;

    if (
      sameSession &&
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.cancelPendingReconnect();
    this.teardownSocket();

    if (!sameSession) {
      this.hasConnectedBefore = false;
      this.reconnectAttempts = 0;
    }

    this.currentRoomId = roomId;
    this.currentUserId = userId;
    this.openSocket();
  }

  /**
   * Manually retry the connection (e.g. from a "Retry" button after the
   * automatic reconnect attempts have been exhausted). Resets the backoff
   * counter and keeps all registered message handlers.
   */
  reconnect() {
    if (!this.currentRoomId || !this.currentUserId) {
      console.error('Cannot reconnect: no previous connection info');
      return;
    }
    this.cancelPendingReconnect();
    this.teardownSocket();
    this.reconnectAttempts = 0;
    this.openSocket();
  }

  /**
   * Intentionally close the connection (leaving the room). Message handlers
   * are NOT cleared here — components own their handlers and remove them in
   * their effect cleanup via removeMessageHandler.
   */
  disconnect() {
    this.cancelPendingReconnect();
    this.teardownSocket();
    this.currentRoomId = null;
    this.currentUserId = null;
    this.hasConnectedBefore = false;
    this.reconnectAttempts = 0;
    this.notifyStatus('disconnected');
  }

  private openSocket() {
    const { currentRoomId: roomId, currentUserId: userId } = this;
    if (!roomId || !userId) return;

    const url = `${config.websocketUrl}?roomId=${encodeURIComponent(roomId)}&userId=${encodeURIComponent(userId)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (error) {
      console.error('Failed to open WebSocket:', error);
      this.scheduleReconnect();
      return;
    }

    this.ws = ws;
    this.notifyStatus(this.hasConnectedBefore ? 'reconnecting' : 'connecting');

    ws.onopen = () => {
      if (this.ws !== ws) return; // socket was superseded
      console.log('WebSocket connected');
      const isReconnect = this.hasConnectedBefore;
      this.hasConnectedBefore = true;
      this.reconnectAttempts = 0;
      this.notifyStatus('connected', { isReconnect });
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.messageHandlers.forEach((handler) => handler(message));
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      // Intentional teardown nulls this.ws first, so this only fires for
      // unexpected closes (network drop, server-side close, failed open).
      if (this.ws !== ws) return;
      this.ws = null;
      console.log('WebSocket disconnected');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (!this.currentRoomId || !this.currentUserId) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.notifyStatus('disconnected');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.notifyStatus('reconnecting');

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  /** Close the current socket without triggering the auto-reconnect logic. */
  private teardownSocket() {
    if (!this.ws) return;
    const ws = this.ws;
    this.ws = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close();
    } catch {
      // Socket may already be closed
    }
  }

  private cancelPendingReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  addMessageHandler(handler: MessageHandler) {
    this.messageHandlers.push(handler);
  }

  removeMessageHandler(handler: MessageHandler) {
    this.messageHandlers = this.messageHandlers.filter((h) => h !== handler);
  }

  /**
   * Subscribe to connection status changes. The listener fires with
   * 'connected' (info.isReconnect distinguishes reconnections), 'connecting',
   * 'reconnecting', or 'disconnected' (reconnect attempts exhausted or
   * intentional disconnect).
   */
  addStatusListener(listener: StatusListener) {
    this.statusListeners.push(listener);
  }

  removeStatusListener(listener: StatusListener) {
    this.statusListeners = this.statusListeners.filter((l) => l !== listener);
  }

  private notifyStatus(state: ConnectionState, info?: StatusInfo) {
    this.statusListeners.forEach((listener) => {
      try {
        listener(state, info);
      } catch (error) {
        console.error('Status listener error:', error);
      }
    });
  }

  send(message: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.ws) return 'DISCONNECTED';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'CONNECTED';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

export default new WebSocketService();
